'use client';

import Link from '@eliadmin/core/src/components/link';

import type { CategoriesEntity } from '@eliadmin/core/src/api/general/types/general-layout-types';

import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetTitle,
  SheetTrigger,
} from '@eliadmin/core/src/components/ui/sheet';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { LuX } from 'react-icons/lu';
import { Button } from '../ui/button';
import { ButtonIcon } from '../ui/button-icon';
import { usePathname } from 'next/navigation';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { ContactLink } from '../contact/contact-link';

export type MobileSidebarProps = {
  className?: {
    trigger?: string;
    content?: string;
    firstSeparator?: string;
    menuList?: string;
    menuLink?: string;
    menuGroup?: string;
    menuSeparator?: string;
    buttonWrapper?: string;
    button?: string;
    iconsWrapper?: string;
    icon?: string;
  };
  side?: 'right' | 'left' | 'bottom' | 'top';
  firstSeparator?: boolean;
  menu?: {
    type: 'page';
    name: string;
    path?: string;
  }[];
  menuArrow?: boolean;
  menuSeparator?: boolean;
  categories?: CategoriesEntity[];
  isScrolled: boolean;
};

const MobileSidebar = (props: MobileSidebarProps) => {
  const { t } = useTranslations();
  const generalLayout = useGlobalStore(state => state.generalLayout);
  const pathname = usePathname();

  return (
    <>
      <Sheet>
        <SheetTrigger
          aria-label="Menu"
          className={cn(
            'flex h-10 w-10 items-center justify-center rounded-full text-primary transition-all ease-in-out',
            props.isScrolled ? 'bg-[#F2F2F2]' : ' bg-white',
            'ea-mobile-sidebar__trigger',
            props.className?.trigger,
          )}
        >
          <ButtonIcon alt="" url="/images/icons/menu-black.svg" />
        </SheetTrigger>
        <SheetContent
          aria-describedby={undefined}
          overlayClassName="bg-black/30 backdrop-blur-none"
          side={props.side}
          className={cn(
            'flex h-screen w-full max-w-[350px] flex-col items-start justify-start bg-white px-5 pb-6 pt-0 sm:max-w-[440px]',
            'ea-mobile-sidebar__content',
            props.className?.content,
          )}
          closeIcon={{
            wrapper: 'hidden',
            icon: 'text-primary h-6 w-6 font-bold',
          }}
        >
          <SheetTitle className="sr-only">Menu</SheetTitle>
          <div className="flex w-full items-center justify-end py-3">
            <SheetClose aria-label="Zavrieť menu" className="flex h-10 w-10 items-center justify-center rounded-full bg-[#F2F2F2]">
              <LuX size={20} />
            </SheetClose>
          </div>
          <div className="flex h-full w-full flex-col items-start justify-between pt-6">
            <div className="flex w-full flex-col items-start justify-start gap-6 pl-4">
              {props?.menu &&
                props.menu.map(field => {
                  const isActive = field?.path === pathname;

                  return (
                    <SheetClose
                      asChild
                      key={field.name}
                      className={cn('h3', 'relative font-medium -tracking-[6%] text-black')}
                    >
                      <Link prefetchOnHover prefetch={false} href={field?.path ?? ''}>
                        {isActive && (
                          <div className="absolute -left-4 top-1/2 max-h-2 min-h-2 min-w-2 max-w-2 -translate-y-1/2 rounded-full bg-primary" />
                        )}
                        {field.name}
                      </Link>
                    </SheetClose>
                  );
                })}
              <Button asChild>
                <Link prefetchOnHover prefetch={false} href={'/kontakt#kontakt'}>
                  <ButtonIcon
                    alt="Mám záujem o projekt"
                    url="/images/icons/bubble-chat.svg"
                    className="h-6 w-6"
                  />
                  Mám záujem o projekt
                </Link>
              </Button>
            </div>
            <div className="flex w-full flex-col items-start justify-start gap-4 rounded-[16px] bg-[#F2F2F2] p-4">
              <ContactLink prefetchOnHover
                prefetch={false}
                href={`tel:${generalLayout.contact?.phone}`}
                target="_blank"
                className="flex w-full items-center justify-start gap-6"
              >
                <div className="flex max-h-[52px] min-h-[52px] min-w-[52px] max-w-[52px] items-center justify-center rounded-[8px] border border-border">
                  <ButtonIcon
                    alt={t('text36')}
                    url="/images/icons/phone-black.svg"
                    className="max-h-6 min-h-6 min-w-6 max-w-6"
                  />
                </div>
                <div className="flex flex-col gap-[6px]">
                  <p className="text-base font-medium -tracking-[6%] text-[#666666]">
                    {t('text800')}
                  </p>
                  <strong className={cn('h5', 'font-medium -tracking-[4%] text-black')}>
                    {generalLayout.contact?.phone}
                  </strong>
                </div>
              </ContactLink>
              <Link prefetchOnHover
                prefetch={false}
                href={`mailto:${generalLayout.contact?.email}`}
                target="_blank"
                className="flex w-full items-center justify-start gap-6"
              >
                <div className="flex max-h-[52px] min-h-[52px] min-w-[52px] max-w-[52px] items-center justify-center rounded-[8px] border border-border">
                  <ButtonIcon
                    alt="Email"
                    url="/images/icons/email-black.svg"
                    className="max-h-6 min-h-6 min-w-6 max-w-6"
                  />
                </div>
                <div className="flex flex-col gap-[6px]">
                  <p className="text-base font-medium -tracking-[6%] text-[#666666]">Napíšte nám</p>
                  <strong className={cn('h5', 'font-medium -tracking-[4%] text-black')}>
                    {generalLayout.contact?.email}
                  </strong>
                </div>
              </Link>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
};

export default MobileSidebar;
